import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { AuthProvider, useAuth } from "../store/AuthContext";
import { ProtectedRoute } from "./ProtectedRoute";
import { PathRedirect } from "./PathRedirect";
import { AdminLayout } from "../components/layout/AdminLayout";
import { CashierLayout } from "../components/layout/CashierLayout";
import { ResCashierLayout } from "../components/layout/ResCashierLayout";
import { RestaurantLayout } from "../components/layout/RestaurantLayout";
import { OrderManagerLayout } from "../components/layout/OrderManagerLayout";

import { LoginPage } from "../features/auth/LoginPage";
import { ForbiddenPage, NotFoundPage } from "../features/system/StatusPages";

import { AdminDashboardPage } from "../features/dashboard/AdminDashboardPage";
import { ProfitDashboardPage } from "../features/dashboard/ProfitDashboardPage";
import { UsersPage } from "../features/users/UsersPage";
import {
  BottleTypesPage,
  BottleCompaniesPage,
} from "../features/bottles/MasterPages";
import { BottleProductsPage } from "../features/bottles/BottleProductsPage";
import { BiteCategoriesPage } from "../features/bites/BiteCategoriesPage";
import { BiteProductsPage } from "../features/bites/BiteProductsPage";
import { StockBatchesPage } from "../features/stock/StockBatchesPage";
import { StockAdjustmentsPage } from "../features/stock/StockAdjustmentsPage";
import { LowStockPage } from "../features/stock/LowStockPage";
import { AdminSalesPage } from "../features/sales/AdminSalesPage";
import { AdminMissedSalePage } from "../features/sales/AdminMissedSalePage";
import { SaleDetailPage } from "../features/sales/SaleDetailPage";
import { CorrectionsAdminPage } from "../features/corrections/CorrectionsAdminPage";
import { ManualSaleCorrectionPage } from "../features/corrections/ManualSaleCorrectionPage";
import { ReportsPage } from "../features/reports/ReportsPage";
import { AuditLogsPage } from "../features/audit/AuditLogsPage";
import { AdminCashSessionsPage } from "../features/cashSessions/AdminCashSessionsPage";
import { RecipientsPage } from "../features/notifications/RecipientsPage";
import { NotificationLogsPage } from "../features/notifications/NotificationLogsPage";
import { WhatsAppNotificationsPage } from "../features/notifications/WhatsAppNotificationsPage";

import { CashierDashboardPage } from "../features/cashier/CashierDashboardPage";
import { ResCashierDashboardPage } from "../features/cashier/ResCashierDashboardPage";
import { CashierSessionPage } from "../features/cashier/CashierSessionPage";
import { BarPOSPage } from "../features/sales/BarPOSPage";
import { RestaurantPOSPage } from "../features/sales/RestaurantPOSPage";
import { MySalesPage } from "../features/sales/MySalesPage";
import { CorrectionRequestPage } from "../features/cashier/CorrectionRequestPage";
import { BackendDownPage } from "../pages/error/BackendDownPage";

import { RestaurantDashboardPage } from "../features/restaurant/RestaurantDashboardPage";
import { RestaurantOrdersPage } from "../features/restaurant/RestaurantOrdersPage";
import { CashSettlementsPage } from "../features/settlements/CashSettlementsPage";
import { AdminLiquorCatalogPage } from "../features/liquor/AdminLiquorCatalogPage";
import { OrderManagerDashboardPage } from "../features/orderManager/OrderManagerDashboardPage";
import { OrderManagerLiquorOrdersPage } from "../features/orderManager/OrderManagerLiquorOrdersPage";
import { OrderManagerLiquorOrderEditorPage } from "../features/orderManager/OrderManagerLiquorOrderEditorPage";
import { OrderManagerOrderStocksPage } from "../features/orderManager/OrderManagerOrderStocksPage";
import { AdminLiquorOrdersPage, AdminLiquorOrderReviewPage } from "../features/liquor/AdminLiquorOrdersPage";

/** Decide landing page based on role. */
const HomeRedirect = () => {
  const { isAuthenticated, role } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (role === "BAR_CASHIER") {
    return <Navigate to="/cashier/bar-sales" replace />;
  }

  if (role === "RES_CASHIER") {
    return <Navigate to="/res-cashier/dashboard" replace />;
  }

  if (role === "RESTAURANT") {
    return <Navigate to="/restaurant/dashboard" replace />;
  }

  if (role === "ORDER_MANAGER") {
    return <Navigate to="/order-manager/dashboard" replace />;
  }

  return <Navigate to="/admin/dashboard" replace />;
};

const SessionRouteSync = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const identity = user ? `${user.userId}:${user.roleName}` : null;
  const previousIdentity = useRef(identity);

  useEffect(() => {
    if (previousIdentity.current !== identity && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
    previousIdentity.current = identity;
  }, [identity, isAuthenticated, navigate]);

  return null;
};

export const AppRoutes = () => (
  <BrowserRouter>
    <AuthProvider>
      <SessionRouteSync />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forbidden" element={<ForbiddenPage />} />
        <Route path="/backend-down" element={<BackendDownPage />} />

        {/* OWNER alias: redirects to the shared admin shell. */}
        <Route
          path="/owner"
          element={<Navigate to="/admin/dashboard" replace />}
        />
        <Route
          path="/owner/dashboard"
          element={<Navigate to="/admin/dashboard" replace />}
        />
        <Route path="/owner/cash-settlements" element={<Navigate to="/admin/cash-settlements" replace />} />
        <Route path="/dashboard" element={<HomeRedirect />} />

        {/* ADMIN + OWNER share the admin shell. Owner is read-only via useCan(), except pages that explicitly allow owner actions. */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={["OWNER", "ADMIN"]}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboardPage />} />
          <Route path="profit" element={<ProfitDashboardPage />} />

          <Route path="bottles/types" element={<BottleTypesPage />} />
          <Route path="bottles/companies" element={<BottleCompaniesPage />} />
          <Route path="bottles/products" element={<BottleProductsPage />} />
          <Route
            path="liquor-catalog"
            element={<ProtectedRoute roles={["ADMIN"]}><AdminLiquorCatalogPage /></ProtectedRoute>}
          />

          <Route path="bites/categories" element={<BiteCategoriesPage />} />
          <Route path="bites/products" element={<BiteProductsPage />} />

          <Route path="stock/batches" element={<StockBatchesPage />} />
          <Route path="stock/adjustments" element={<StockAdjustmentsPage />} />
          <Route path="stock/low-stock" element={<LowStockPage />} />

          <Route path="sales" element={<AdminSalesPage />} />
          <Route path="restaurant-orders" element={<RestaurantOrdersPage />} />
          <Route path="liquor-orders" element={<AdminLiquorOrdersPage />} />
          <Route path="liquor-orders/:id" element={<AdminLiquorOrderReviewPage />} />

          <Route
            path="sales/missed"
            element={
              <ProtectedRoute roles={["ADMIN"]}>
                <AdminMissedSalePage />
              </ProtectedRoute>
            }
          />

          <Route path="sales/:saleId" element={<SaleDetailPage context="admin" />} />

          <Route
            path="sales/:saleId/manual-correction"
            element={
              <ProtectedRoute roles={["ADMIN"]}>
                <ManualSaleCorrectionPage />
              </ProtectedRoute>
            }
          />

          <Route path="corrections" element={<CorrectionsAdminPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="cash-sessions" element={<AdminCashSessionsPage />} />
          <Route path="cash-settlements" element={<CashSettlementsPage />} />
          <Route path="owner-settlements" element={<Navigate to="/admin/cash-settlements?section=history" replace />} />

          {/* ADMIN-only administration routes. */}
          <Route
            path="users"
            element={
              <ProtectedRoute roles={["ADMIN"]}>
                <UsersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="audit/logs"
            element={
              <ProtectedRoute roles={["ADMIN"]}>
                <AuditLogsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="notifications/recipients"
            element={
              <ProtectedRoute roles={["ADMIN"]}>
                <RecipientsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="notifications/logs"
            element={
              <ProtectedRoute roles={["ADMIN"]}>
                <NotificationLogsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="notifications/whatsapp"
            element={
              <ProtectedRoute roles={["ADMIN"]}>
                <WhatsAppNotificationsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="/order-manager"
          element={
            <ProtectedRoute roles={["ORDER_MANAGER"]}>
              <OrderManagerLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/order-manager/dashboard" replace />} />
          <Route path="dashboard" element={<OrderManagerDashboardPage />} />
          <Route path="liquor-orders" element={<OrderManagerLiquorOrdersPage />} />
          <Route path="order-stocks" element={<OrderManagerOrderStocksPage />} />
          <Route path="liquor-orders/new" element={<OrderManagerLiquorOrderEditorPage />} />
          <Route path="liquor-orders/:id" element={<OrderManagerLiquorOrderEditorPage />} />
        </Route>

        {/* RES_CASHIER receives only restaurant POS and its cash session. */}
        <Route
          path="/res-cashier"
          element={
            <ProtectedRoute roles={["RES_CASHIER"]}>
              <ResCashierLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/res-cashier/dashboard" replace />} />
          <Route path="dashboard" element={<ResCashierDashboardPage />} />
          <Route path="pos" element={<RestaurantPOSPage />} />
          <Route path="orders" element={<RestaurantOrdersPage />} />
          <Route path="cash-session" element={<CashierSessionPage />} />
          <Route path="cash-settlements" element={<CashSettlementsPage />} />
          <Route path="settlements" element={<Navigate to="/res-cashier/cash-settlements?section=settlement" replace />} />
          <Route path="expenses" element={<Navigate to="/res-cashier/cash-settlements?section=expenses" replace />} />
          <Route
            path="my-sales"
            element={
              <MySalesPage
                posPath="/res-cashier/pos"
                saleDetailPath={(saleId) => `/res-cashier/sales/${saleId}`}
                paginationStorageKey="res-cashier-my-sales"
              />
            }
          />
          <Route
            path="sales/:saleId"
            element={
              <SaleDetailPage
                context="cashier"
                cashierBasePath="/res-cashier"
                cashierMySalesPath="/res-cashier/my-sales"
              />
            }
          />
          <Route
            path="sales/:saleId/correction"
            element={<CorrectionRequestPage cashierBasePath="/res-cashier" />}
          />
        </Route>

        {/* BAR_CASHIER shell — cashier-only. */}
        <Route
          path="/cashier"
          element={
            <ProtectedRoute roles={["BAR_CASHIER"]}>
              <CashierLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/cashier/bar-sales" replace />} />
          <Route path="dashboard" element={<CashierDashboardPage />} />
          <Route path="bar-sales" element={<BarPOSPage />} />
          <Route path="cash-session" element={<CashierSessionPage />} />
          <Route path="sales/my" element={<MySalesPage />} />
          <Route path="sales/:saleId" element={<SaleDetailPage context="cashier" />} />
          <Route path="sales/:saleId/correction" element={<CorrectionRequestPage />} />
          <Route path="cash-settlements" element={<CashSettlementsPage />} />
          <Route path="owner-settlement" element={<Navigate to="/cashier/cash-settlements?section=settlement" replace />} />
          <Route path="expenses" element={<Navigate to="/cashier/cash-settlements?section=expenses" replace />} />
        </Route>

        {/* RESTAURANT shell — restaurant user only. */}
        <Route
          path="/restaurant"
          element={
            <ProtectedRoute roles={["RESTAURANT"]}>
              <RestaurantLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/restaurant/dashboard" replace />} />
          <Route path="dashboard" element={<RestaurantDashboardPage />} />
          <Route path="cash-settlements" element={<CashSettlementsPage />} />
          <Route path="settlements" element={<Navigate to="/restaurant/cash-settlements?section=history" replace />} />
          <Route path="owner-settlement" element={<Navigate to="/restaurant/cash-settlements?section=history" replace />} />
          <Route path="expenses" element={<Navigate to="/restaurant/cash-settlements?section=expenses" replace />} />
        </Route>


        {/* Legacy /pos/* aliases for backward compatibility. */}
        <Route path="/pos" element={<Navigate to="/cashier/bar-sales" replace />} />
        <Route
          path="/pos/dashboard"
          element={<Navigate to="/cashier/dashboard" replace />}
        />
        <Route
          path="/pos/sales/new"
          element={<Navigate to="/cashier/bar-sales" replace />}
        />
        <Route
          path="/pos/cash-session"
          element={<Navigate to="/cashier/cash-session" replace />}
        />
        <Route
          path="/pos/sales/my"
          element={<Navigate to="/cashier/sales/my" replace />}
        />
        <Route
          path="/pos/sales/:saleId"
          element={<PathRedirect to="/cashier/sales/:saleId" />}
        />
        <Route
          path="/pos/sales/:saleId/correction"
          element={<PathRedirect to="/cashier/sales/:saleId/correction" />}
        />

        <Route path="/" element={<HomeRedirect />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AuthProvider>
  </BrowserRouter>
);
